import axiosClient from './axiosClient';

/**
 * Typed axios wrappers for `/api/admin/globals/cohorts`.
 *
 * Backend contract mirrors `/api/admin/globals/tasks` and `/adventures` —
 * the same 3-phase versioning model (draft `current_version` → publish →
 * tenants sync). Keep this file in lockstep with the route handlers —
 * in particular the PATCH body's `base_version` + 409
 * `{error:'version_conflict', current_version}` shape, which the edit
 * page relies on to distinguish a stale-save from any other failure.
 *
 * Round 5 additions vs. adventures:
 * - `cohort_rule_json` — JSONB rule blob (filter predicates, eligibility
 *   gates). Plain textarea for R5; Monaco lands when component_type /
 *   onboarding_component pull it in.
 * - Drops `language_code` — cohorts aren't localized.
 */

/** History-row shape returned inside the GET /:code envelope. */
export interface CohortHistoryRow {
  version: number;
  cohort_name: string | null;
  cohort_rule_json: object | null;
  platform_cohort_id: string | number | null;
  active: boolean;
  change_summary: string | null;
  change_type: 'create' | 'update' | 'delete' | 'publish' | 'rollback';
  created_at: string;
  created_by: string | null;
}

export interface AdminCohortRow {
  cohort_code: string;
  cohort_name: string | null;
  cohort_rule_json: object | null;
  platform_cohort_id: string | number | null;
  active: boolean;
  current_version: number;
  updated_at: string;
  updated_by: string | null;
  status: 'in_sync' | 'never_published' | 'unpublished';
}

export interface AdminCohortDetail extends AdminCohortRow {
  history: CohortHistoryRow[];
}

export interface ListCohortsResponse {
  rows: AdminCohortRow[];
}

export interface ListCohortsParams {
  sort?: 'cohort_code' | 'cohort_name' | 'updated_at' | 'current_version';
  dir?: 'asc' | 'desc';
  status?: 'in_sync' | 'never_published';
}

export interface CreateCohortPayload {
  cohort_code: string;
  cohort_name: string;
  cohort_rule_json?: object | null;
  change_summary?: string;
}

/** An unlinked platform cohort (in cohort.cohort, not in the config catalog and
 *  not tenant-linked) that can be adopted into the catalog. */
export interface OrphanPlatformCohort {
  cohort_code: string;
  cohort_name: string;
  platform_cohort_id: string;
  cohort_enabled: boolean;
  create_user: string | null;
  create_ts: string;
  /** true → the catalog already has this name under another code (adopt blocked). */
  name_taken: boolean;
}

export interface UpdateCohortPayload {
  base_version: number;
  cohort_name?: string;
  cohort_rule_json?: object | null;
  active?: boolean;
  change_summary?: string;
}

/* ------------------------------------------------------------------ */
/*  Publish + Rollback                                                  */
/* ------------------------------------------------------------------ */

export interface PublishCohortEntityRequest {
  cohort_code: string;
  base_version: number;
}

export interface PublishCohortsRequest {
  entities: PublishCohortEntityRequest[];
  batch_id?: string;
  change_summary?: string;
}

export interface PublishCohortEntityResult {
  cohort_code: string;
  ok: boolean;
  published_version?: number;
  error?: string;
  message?: string;
  current_version?: number;
}

export interface PublishCohortsResponse {
  batch_id: string;
  results: PublishCohortEntityResult[];
}

export interface RollbackCohortPayload {
  target_version: number;
  base_version: number;
  change_summary?: string;
}

export const adminCohortApi = {
  list: async (params: ListCohortsParams = {}): Promise<AdminCohortRow[]> => {
    const res = await axiosClient.get<ListCohortsResponse>(
      '/api/admin/globals/cohorts',
      { params },
    );
    return res.data.rows;
  },

  get: async (code: string): Promise<AdminCohortDetail> => {
    const res = await axiosClient.get<AdminCohortDetail>(
      `/api/admin/globals/cohorts/${encodeURIComponent(code)}`,
    );
    return res.data;
  },

  /** Unlinked platform cohorts available to adopt into the config catalog. */
  listOrphans: async (): Promise<OrphanPlatformCohort[]> => {
    const res = await axiosClient.get<{ rows: OrphanPlatformCohort[] }>(
      '/api/admin/globals/cohorts/orphans',
    );
    return res.data.rows ?? [];
  },

  /** Adopt an unlinked platform cohort into the catalog (reuses its code). */
  adopt: async (code: string): Promise<AdminCohortRow> => {
    const res = await axiosClient.post<AdminCohortRow>(
      `/api/admin/globals/cohorts/${encodeURIComponent(code)}/adopt`,
      {},
    );
    return res.data;
  },

  create: async (payload: CreateCohortPayload): Promise<AdminCohortRow> => {
    const res = await axiosClient.post<AdminCohortRow>(
      '/api/admin/globals/cohorts',
      payload,
    );
    return res.data;
  },

  update: async (
    code: string,
    payload: UpdateCohortPayload,
  ): Promise<AdminCohortRow> => {
    const res = await axiosClient.patch<AdminCohortRow>(
      `/api/admin/globals/cohorts/${encodeURIComponent(code)}`,
      payload,
    );
    return res.data;
  },

  publish: async (payload: PublishCohortsRequest): Promise<PublishCohortsResponse> => {
    const res = await axiosClient.post<PublishCohortsResponse>(
      '/api/admin/globals/cohorts/publish',
      payload,
    );
    return res.data;
  },

  publishOne: async (
    code: string,
    base_version: number,
    options?: { batch_id?: string; change_summary?: string },
  ): Promise<PublishCohortsResponse> => {
    return adminCohortApi.publish({
      entities: [{ cohort_code: code, base_version }],
      batch_id: options?.batch_id,
      change_summary: options?.change_summary,
    });
  },

  rollback: async (
    code: string,
    payload: RollbackCohortPayload,
  ): Promise<AdminCohortRow> => {
    const res = await axiosClient.post<AdminCohortRow>(
      `/api/admin/globals/cohorts/${encodeURIComponent(code)}/rollback`,
      payload,
    );
    return res.data;
  },
};

/**
 * Message for a 409 `name_conflict` from create / rename (PLT-3759), or null when
 * the error is something else. Mirrors the wording of the adopt dialog.
 */
export function cohortNameConflictMessage(err: unknown): string | null {
  const data = (err as { response?: { status?: number; data?: { error?: string; cohort_name?: string; existing_code?: string } } })
    ?.response?.data;
  if (data?.error !== 'name_conflict') return null;
  const name = data.cohort_name ? `"${data.cohort_name}"` : 'This name';
  return `${name} is already used by another cohort (${data.existing_code ?? 'existing'}). Choose a different name.`;
}
